import { startTransition, useEffect, useEffectEvent, useState } from "react";
import type {
  CampaignResultStatus,
  CampaignResultsRecipientsResponse,
  CampaignResultsSummary,
  CampaignStatusName,
} from "@multi-chat/shared";
import {
  getCampaignResultsSummary,
  listCampaignResultsRecipients,
} from "../../api/campaigns-api.js";

/** Bounded poll while RUNNING or provider-pending after COMPLETED. */
export const CAMPAIGN_RESULTS_POLL_INTERVAL_MS = 4_000;

export function shouldShowCampaignResults(status: CampaignStatusName): boolean {
  return (
    status === "RUNNING" ||
    status === "COMPLETED" ||
    status === "FAILED" ||
    status === "CANCELLED"
  );
}

export function useCampaignResults(input: {
  organizationId: number;
  campaignId: number;
  campaignStatus: CampaignStatusName;
  enabled: boolean;
}): {
  summary: CampaignResultsSummary | null;
  recipients: CampaignResultsRecipientsResponse | null;
  summaryError: unknown;
  recipientsError: unknown;
  loading: boolean;
  page: number;
  pageSize: 25 | 50 | 100;
  search: string;
  resultStatus: CampaignResultStatus | "ALL";
  setPage: (page: number) => void;
  setPageSize: (pageSize: 25 | 50 | 100) => void;
  setSearch: (search: string) => void;
  setResultStatus: (status: CampaignResultStatus | "ALL") => void;
  refresh: () => void;
} {
  const [summary, setSummary] = useState<CampaignResultsSummary | null>(null);
  const [recipients, setRecipients] = useState<CampaignResultsRecipientsResponse | null>(null);
  const [summaryError, setSummaryError] = useState<unknown>(null);
  const [recipientsError, setRecipientsError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<25 | 50 | 100>(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [resultStatus, setResultStatus] = useState<CampaignResultStatus | "ALL">("ALL");
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, resultStatus, pageSize, input.campaignId]);

  const load = useEffectEvent(async (signal: AbortSignal) => {
    if (!input.enabled) return;
    setLoading(true);
    setSummaryError(null);
    setRecipientsError(null);
    try {
      const [nextSummary, nextRecipients] = await Promise.all([
        getCampaignResultsSummary(input.organizationId, input.campaignId, signal),
        listCampaignResultsRecipients(
          input.organizationId,
          input.campaignId,
          {
            page,
            pageSize,
            search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
            resultStatus: resultStatus === "ALL" ? undefined : resultStatus,
          },
          signal,
        ),
      ]);
      if (signal.aborted) return;
      startTransition(() => {
        setSummary(nextSummary);
        setRecipients(nextRecipients);
      });
    } catch (error) {
      if (signal.aborted) return;
      setSummaryError(error);
      setRecipientsError(error);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  useEffect(() => {
    if (!input.enabled) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [
    input.enabled,
    input.organizationId,
    input.campaignId,
    page,
    pageSize,
    debouncedSearch,
    resultStatus,
    refreshToken,
  ]);

  useEffect(() => {
    if (!input.enabled) return;
    const shouldPoll =
      input.campaignStatus === "RUNNING" || summary?.providerUpdatesPending === true;
    if (!shouldPoll) return;
    const handle = window.setInterval(() => {
      setRefreshToken((token) => token + 1);
    }, CAMPAIGN_RESULTS_POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [
    input.enabled,
    input.campaignStatus,
    summary?.providerUpdatesPending,
  ]);

  return {
    summary,
    recipients,
    summaryError,
    recipientsError,
    loading,
    page,
    pageSize,
    search,
    resultStatus,
    setPage,
    setPageSize,
    setSearch,
    setResultStatus,
    refresh: () => setRefreshToken((token) => token + 1),
  };
}
